import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import EventDetailModal from './components/EventDetailModal';
import StructuredData from '@/components/feature/StructuredData';
import { FrontendEvent } from '@/services/transform';
import { getPublicEvents } from '@/services/events.api';
import { getPublicPlans, Plan } from '@/services/plans.api';
import { EventType } from '@/mocks/events';

const HERO_BG = '/images/hero-bg-events.jpg';

const TYPE_COLORS: Record<string, string> = {
  offline: 'bg-emerald-100 text-emerald-700',
  online: 'bg-sky-100 text-sky-700',
  hybrid: 'bg-amber-100 text-amber-700',
};

const MONTHS_ZH = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseLocalDate(s: string): Date {
  // YYYY-MM-DD 视为纽约时区的纯日期，避免 UTC 解析导致显示日期偏移
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

const MONTH_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(dateStr: string, endDate: string, lang: string): string {
  const start = parseLocalDate(dateStr);
  const end = parseLocalDate(endDate);
  if (lang === 'zh') {
    return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 – ${end.getDate()}日 (纽约时间)`;
  }
  return `${MONTH_SHORT_EN[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()} (ET)`;
}

interface EventCardProps {
  event: FrontendEvent;
  isZh: boolean;
  onDetail: (event: FrontendEvent) => void;
  onRegister: (event: FrontendEvent) => void;
}

function EventCard({ event, isZh, onDetail, onRegister }: EventCardProps) {
  const { t } = useTranslation();
  return (
    <article className="group bg-white border border-[#0A2540]/10 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-200">
      <div className="relative w-full h-48 overflow-hidden cursor-pointer bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#2C7DA0] flex items-center justify-center" onClick={() => onDetail(event)}>
        <img
          src={event.cover}
          alt={isZh ? event.title : event.titleEn}
          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[event.type]}`}>
            {t(`event.type.${event.type}`)}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3
          className="font-bold text-[#0A2540] text-lg mb-3 leading-snug cursor-pointer hover:text-[#2C7DA0] transition-colors"
          onClick={() => onDetail(event)}
        >
          {isZh ? event.title : event.titleEn}
        </h3>
        <div className="flex flex-col gap-1.5 mb-4 text-sm text-[#0A2540]/50">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-[#2C7DA0]" />
            </div>
            <span>{formatDate(event.date, event.endDate, isZh ? 'zh' : 'en')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-[#2C7DA0]" />
            </div>
            <span>{isZh ? event.location : event.locationEn}</span>
          </div>
        </div>
        <p className="text-[#0A2540]/60 text-sm leading-relaxed line-clamp-2 mb-4">
          {isZh ? event.description : event.descriptionEn}
        </p>
        <div className="flex flex-wrap gap-1 mb-5">
          {(isZh ? event.tags : event.tagsEn).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-[#F7F9FC] text-[#0A2540]/50 text-xs rounded-md">
              {tag}
            </span>
          ))}
        </div>
        {/* Registration deadline / window badge */}
        {(() => {
          const ae = event as AdminEvent;
          const today = new Date().toISOString().slice(0, 10);
          if (ae.registrationDeadline) {
            const dl = ae.registrationDeadline;
            const mo = parseInt(dl.slice(5, 7), 10);
            const da = parseInt(dl.slice(8, 10), 10);
            const isPast = today > dl;
            return (
              <div className={`flex items-center gap-1.5 text-xs mb-3 font-medium ${isPast ? 'text-red-500' : 'text-amber-600'}`}>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={isPast ? 'ri-close-circle-line' : 'ri-time-line'} />
                </div>
                {isPast ? '报名已截止' : `报名截止 ${mo}月${da}日`}
              </div>
            );
          }
          if (ae.registrationStart && today < ae.registrationStart) {
            const rs = ae.registrationStart;
            const mo = parseInt(rs.slice(5, 7), 10);
            const da = parseInt(rs.slice(8, 10), 10);
            return (
              <div className="flex items-center gap-1.5 text-xs mb-3 font-medium text-[#6B7280]">
                <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-lock-line" /></div>
                {`报名开放 ${mo}月${da}日`}
              </div>
            );
          }
          return null;
        })()}
        <div className="flex items-center gap-3">
          {event.status !== 'past' && (
            <button
              onClick={() => onRegister(event)}
              className="flex-1 px-4 py-2.5 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('eventsPage.register')}
            </button>
          )}
          <button
            onClick={() => onDetail(event)}
            className={event.status === 'past' ? 'flex-1 ' : '' + 'px-4 py-2.5 border border-[#0A2540]/20 hover:border-[#2C7DA0] text-[#0A2540]/70 hover:text-[#2C7DA0] rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap'}
          >
            {event.status === 'past' ? t('eventsPage.viewPast') : t('eventsPage.learnMore')}
          </button>
        </div>
      </div>
    </article>
  );
}

function FuturePlansTimeline() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await getPublicPlans();
        setPlans(data);
      } catch (error) {
        console.error('获取活动计划失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-20 bg-[#0A2540]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="animate-pulse text-white/50">Loading plans...</div>
        </div>
      </section>
    );
  }

  if (plans.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-20 bg-[#0A2540]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line" />
            </div>
            {t('eventsPage.plan.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('eventsPage.plan.title')}
          </h2>
          <p className="text-white/50 max-w-lg mx-auto text-base">
            {t('eventsPage.plan.subtitle')}
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 -translate-x-0.5 top-0 bottom-0 w-px bg-[#2C7DA0]/30" />
          <div className="flex flex-col gap-8 md:gap-0">
            {plans.map((plan, idx) => {
              const isLeft = idx % 2 === 0;
              return (
                <div key={plan.id} className={`relative flex flex-col md:flex-row ${isLeft ? '' : 'md:flex-row-reverse'} items-start md:items-center gap-4 md:gap-0`}>
                  <div className={`md:w-[calc(50%-2rem)] ${isLeft ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                      <div className="text-[#2C7DA0] text-xs font-semibold mb-2 tracking-widest uppercase">
                        {isZh ? plan.period : plan.period_en}
                      </div>
                      <h3 className="font-bold text-white text-lg mb-2">
                        {isZh ? plan.title : plan.title_en}
                      </h3>
                      <p className="text-white/60 text-sm mb-2">{isZh ? plan.theme : plan.theme_en}</p>
                      <div className="flex items-center gap-1 text-white/40 text-xs">
                        <i className="ri-user-line" />
                        <span>{isZh ? plan.audience : plan.audience_en}</span>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#2C7DA0] border-4 border-[#0A2540]" />
                  <div className="md:w-[calc(50%-2rem)]" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function EventsPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('past');
  const [selectedType, setSelectedType] = useState<EventType | 'all'>('all');
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [visible, setVisible] = useState(false);
  const [modalEvent, setModalEvent] = useState<FrontendEvent | null>(null);
  const [modalDefaultTab, setModalDefaultTab] = useState<'overview' | 'agenda' | 'speakers' | 'register'>('overview');
  const ref = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  // Load events from API
  const [allEvents, setAllEvents] = useState<FrontendEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPublicEvents().then(events => {
      setAllEvents(events);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  const filteredEvents = allEvents.filter((e) => {
    if (e.status !== activeTab) return false;
    if (selectedType !== 'all' && e.type !== selectedType) return false;
    if (selectedMonth !== 'all' && e.month !== selectedMonth) return false;
    if (selectedRegion !== 'all' && e.region !== selectedRegion) return false;
    return true;
  });

  // Build Schema.org Event structured data
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'GTRA Global Events',
    description: 'Global Technology Roundtable Alliance events - international science and technology innovation forums, summits and symposiums',
    itemListElement: allEvents.map((ev, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Event',
        name: ev.titleEn,
        description: ev.descriptionEn,
        startDate: ev.date,
        endDate: ev.endDate,
        eventStatus: ev.status === 'upcoming'
          ? 'https://schema.org/EventScheduled'
          : 'https://schema.org/EventPast',
        eventAttendanceMode: ev.type === 'online'
          ? 'https://schema.org/OnlineEventAttendanceMode'
          : ev.type === 'hybrid'
            ? 'https://schema.org/MixedEventAttendanceMode'
            : 'https://schema.org/OfflineEventAttendanceMode',
        location: {
          '@type': 'Place',
          name: ev.locationEn,
          address: { '@type': 'PostalAddress', addressLocality: ev.locationEn, addressCountry: ev.country },
        },
        organizer: {
          '@type': 'Organization',
          name: 'Global Tech Roundtable Alliance (GTRA)',
          url: 'https://gtraweb3.com',
        },
        keywords: ev.tagsEn.join(', '),
      },
    })),
  }), []);

  // ... existing code ...

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const months = Array.from(new Set(allEvents.map((e) => e.month))).sort((a, b) => a - b);

  const typeOptions: { value: EventType | 'all'; labelZh: string; labelEn: string }[] = [
    { value: 'all', labelZh: '全部', labelEn: 'All' },
    { value: 'offline', labelZh: '线下', labelEn: 'In-Person' },
    { value: 'online', labelZh: '线上', labelEn: 'Online' },
    { value: 'hybrid', labelZh: '混合', labelEn: 'Hybrid' },
  ];

  const regionOptions = [
    { value: 'all', labelZh: '全部地区', labelEn: 'All Regions' },
    { value: 'americas', labelZh: '美洲', labelEn: 'Americas' },
    { value: 'asia', labelZh: '亚洲', labelEn: 'Asia' },
    { value: 'europe', labelZh: '欧洲', labelEn: 'Europe' },
  ];

  const openDetail = (event: FrontendEvent) => {
    setModalDefaultTab('overview');
    setModalEvent(event);
  };

  const openRegister = (event: FrontendEvent) => {
    setModalDefaultTab('register');
    setModalEvent(event);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <StructuredData data={structuredData} id="events-list" />

      {/* Hero */}
      <section className="relative h-64 md:h-80 flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 to-[#0A2540]/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-event-line" />
            </div>
            {t('events.home.badge')}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            {t('eventsPage.hero.title')}
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl">
            {t('eventsPage.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Events Content */}
      <section className="py-12 md:py-16" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Tabs + Filters */}
          <div className={`bg-white rounded-2xl p-5 mb-8 flex flex-col gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-1 bg-[#F7F9FC] p-1 rounded-xl w-fit">
              {['past', 'upcoming'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as 'upcoming' | 'past')}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap
                    ${activeTab === tab ? 'bg-white text-[#0A2540]' : 'text-[#0A2540]/50 hover:text-[#0A2540]'}`}
                >
                  {t(`eventsPage.${tab}`)}
                </button>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">{t('eventsPage.filter.type')}:</span>
                {typeOptions.map(({ value, labelZh, labelEn }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedType(value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap
                      ${selectedType === value ? 'bg-[#0A2540] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#0A2540]/10'}`}
                  >
                    {isZh ? labelZh : labelEn}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">{t('eventsPage.filter.month')}:</span>
                <button
                  onClick={() => setSelectedMonth('all')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap
                    ${selectedMonth === 'all' ? 'bg-[#2C7DA0] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#2C7DA0]/10'}`}
                >
                  {isZh ? '全部' : 'All'}
                </button>
                {months.map((m) => (
                  <button
                    key={m}
                    onClick={() => setSelectedMonth(m)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap
                      ${selectedMonth === m ? 'bg-[#2C7DA0] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#2C7DA0]/10'}`}
                  >
                    {isZh ? MONTHS_ZH[m - 1] : MONTHS_EN[m - 1]}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">{t('eventsPage.filter.region')}:</span>
                {regionOptions.map(({ value, labelZh, labelEn }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedRegion(value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap
                      ${selectedRegion === value ? 'bg-emerald-600 text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-emerald-50'}`}
                  >
                    {isZh ? labelZh : labelEn}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Events Grid */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-48 bg-gray-200 rounded-xl mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-20 bg-gray-200 rounded mb-4" />
                    <div className="h-10 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : filteredEvents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isZh={isZh}
                    onDetail={openDetail}
                    onRegister={openRegister}
                  />
                ))}
              </div>
            ) : !loading && (
              <div className="text-center py-20 text-[#0A2540]/40">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-close-line text-4xl" />
                </div>
                <p className="text-base">{t('eventsPage.noEvents')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <FuturePlansTimeline />
      <Footer />

      {/* Event Detail Modal */}
      {modalEvent && (
        <EventDetailModal
          event={modalEvent}
          onClose={() => setModalEvent(null)}
          defaultTab={modalDefaultTab}
        />
      )}
    </div>
  );
}
